import React, { useState } from 'react';

export default function DeliveryProofSection({ deliveryTask }) {
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  if (!deliveryTask) {
    return null;
  }

  const getStatusBadgeStyle = (status) => {
    const styles = {
      'A_PREPARER': 'bg-yellow-100 text-yellow-800 border-yellow-300',
      'EN_LIVRAISON': 'bg-blue-100 text-blue-800 border-blue-300',
      'LIVRE': 'bg-green-100 text-green-800 border-green-300',
      'PROBLEME': 'bg-red-100 text-red-800 border-red-300'
    };
    return styles?.[status] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'A_PREPARER': 'À préparer',
      'EN_LIVRAISON': 'En livraison',
      'LIVRE': 'Livré',
      'INCIDENT': 'Incident de livraison',
      'PROBLEME': 'Problème'
    };
    return labels?.[status] || status;
  };

  // NEW: Function to get readable incident message
  const getIncidentMessage = (incidentType) => {
    const messages = {
      'CLIENT_ABSENT': 'Le destinataire était absent lors de la tentative de livraison',
      'REFUS_CLIENT': 'Le destinataire a refusé de recevoir le colis',
      'ADRESSE_INTROUVABLE': 'L\'adresse de livraison n\'a pas pu être localisée',
      'ADRESSE_INCORRECTE': 'L\'adresse fournie est incorrecte ou incomplète',
      'COLIS_ENDOMMAGE': 'Le colis a été endommagé pendant le transport',
      'DESTINATAIRE_INJOIGNABLE': 'Impossible de contacter le destinataire',
      'ACCES_DIFFICILE': 'Accès difficile ou zone inaccessible',
      'PROBLEME_DOUANE': 'Problème lors du dédouanement',
      'AUTRE': 'Un incident est survenu lors de la livraison'
    };
    return messages?.[incidentType] || 'Un incident est survenu lors de la livraison';
  };

  const deliveryPhotos = Array.isArray(deliveryTask?.delivery_photo_urls) 
    ? deliveryTask?.delivery_photo_urls 
    : [];

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-lg font-bold text-gray-900 mb-4">Statut de livraison Abidjan</h2>
      
      {/* Status */}
      <div className="mb-6">
        <h3 className="text-sm font-medium text-gray-500 mb-2">Statut actuel</h3>
        <span className={`inline-flex items-center px-4 py-2 rounded-full border text-sm font-medium ${getStatusBadgeStyle(deliveryTask?.status)}`}>
          {getStatusLabel(deliveryTask?.status)}
        </span>
      </div>

      {/* NEW: Incident Information Section - ONLY if status is INCIDENT */}
      {deliveryTask?.status === 'INCIDENT' && deliveryTask?.incident_type && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start">
            <svg className="w-6 h-6 text-red-600 mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-red-900 mb-2">Incident signalé</h3>
              <p className="text-sm text-red-800 mb-3">
                {getIncidentMessage(deliveryTask?.incident_type)}
              </p>
              {deliveryTask?.updated_at && (
                <div className="flex items-center text-xs text-red-700">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span>
                    Date de l'incident : {new Date(deliveryTask?.updated_at)?.toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
              )}
              <div className="mt-3 pt-3 border-t border-red-200">
                <p className="text-xs text-red-700">
                  Notre équipe travaille activement pour résoudre ce problème. Vous serez informé dès qu'une nouvelle tentative de livraison sera programmée.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delivery Date - ONLY show if actually delivered */}
      {deliveryTask?.delivered_at && deliveryTask?.status === 'LIVRE' && (
        <div className="mb-6">
          <h3 className="text-sm font-medium text-gray-500 mb-2">Date de livraison</h3>
          <div className="flex items-center text-gray-900">
            <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="text-sm">
              {new Date(deliveryTask.delivered_at)?.toLocaleString('fr-FR', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </span>
          </div>
        </div>
      )}

      {/* Delivery Photos Gallery */}
      {deliveryPhotos?.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-medium text-gray-500 mb-3">Photos de livraison</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {deliveryPhotos?.map((photoUrl, index) => (
              <div 
                key={index}
                className="relative group cursor-pointer rounded-lg overflow-hidden border border-gray-200 hover:border-green-500 transition-all"
                onClick={() => setSelectedPhoto(photoUrl)}
              >
                <img
                  src={photoUrl}
                  alt={`Photo de livraison ${index + 1}`}
                  className="w-full h-32 object-cover group-hover:scale-105 transition-transform"
                />
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity flex items-center justify-center">
                  <svg className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                  </svg>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Delivery Signature */}
      {deliveryTask?.delivery_signature_url && (
        <div className="mb-6">
          <h3 className="text-sm font-medium text-gray-500 mb-3">Signature du destinataire</h3>
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <img
              src={deliveryTask?.delivery_signature_url}
              alt="Signature de livraison"
              className="max-w-full h-auto max-h-40 mx-auto"
            />
          </div>
        </div>
      )}

      {/* Empty State - updated message */}
      {deliveryTask?.status !== 'INCIDENT' && !deliveryTask?.delivered_at && deliveryPhotos?.length === 0 && !deliveryTask?.delivery_signature_url && (
        <div className="text-center py-8 text-gray-500">
          <svg className="w-16 h-16 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="text-sm">Les informations de livraison seront disponibles une fois la livraison effectuée</p>
        </div>
      )}

      {/* Photo Zoom Modal */}
      {selectedPhoto && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <div className="relative max-w-5xl max-h-full">
            <button
              onClick={() => setSelectedPhoto(null)}
              className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <img
              src={selectedPhoto}
              alt="Photo de livraison agrandie"
              className="max-w-full max-h-screen object-contain"
              onClick={(e) => e?.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
}